import React from 'react';
import { createPortal } from 'react-dom';
import UiCardImage from '../../../ui/CardImage';
import { isLandscape } from '../../../ui/cardOrientation';

const PREVIEW_IMAGE_WIDTH = 360;
const PREVIEW_IMAGE_HEIGHT = PREVIEW_IMAGE_WIDTH * (680 / 488);

/**
 * Maße der Vorschau: hochkant 360 × 504, bei einer Querformat-Karte dieselbe lange Kante quer,
 * also 504 × 360. Die Vorschau zeigt nur die Vorderseite, deshalb zählt deren Ausrichtung.
 */
export function previewDimensions(card) {
  if (isLandscape(card?.imageOrientation, 'front')) {
    return { width: (PREVIEW_IMAGE_WIDTH * 7) / 5, height: PREVIEW_IMAGE_WIDTH };
  }
  return { width: PREVIEW_IMAGE_WIDTH, height: PREVIEW_IMAGE_HEIGHT };
}

/**
 * Berechnet die Position der Kartenvorschau: rechts-unten vom Cursor.
 * Fallbacks: links wenn kein Platz rechts, oben wenn kein Platz unten.
 */
function calculatePreviewPosition(mouseX, mouseY, previewWidth, previewHeight) {
  const padding = 25;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Horizontal: rechts neben dem Cursor
  let x = mouseX + padding;

  // Fallback: links neben dem Cursor wenn rechts kein Platz
  if (x + previewWidth + padding > viewportWidth) {
    x = mouseX - previewWidth - padding;
  }

  // Sicherheit: nicht über den linken Rand hinaus
  if (x < padding) x = padding;

  // Vertikal: unterhalb des Mauszeigers
  let y = mouseY + padding;

  // Fallback: oberhalb der Maus wenn unten kein Platz
  if (y + previewHeight + padding > viewportHeight) {
    y = mouseY - previewHeight - padding;
  }

  // Sicherheit: nicht über den oberen Rand hinaus
  if (y < padding) y = padding;

  return { x, y };
}

/**
 * Hover-Kartenvorschau, gerendert per Portal am `document.body`.
 *
 * Das Portal ist Bedingung, nicht Geschmack: Die Matte `.de-playmat` weicht ihre Ränder mit
 * `mask-image` auf, und eine Maske öffnet eine eigene Ebenen-Gruppe. Bleibt die Vorschau im
 * Editor-Baum, zählt ihr z-index nur innerhalb der Matte — sie rutscht unter die Filterleiste
 * und wird an den Matten-Rändern mit ausgeblendet.
 */
export default function CardPreview({ card, position }) {
  // Randbedingung: Touch-Browser synthetisieren beim Antippen mouseenter/mousemove. Die Vorschau
  // erschiene dann beim Tipp. Entfernt der Tipp das auslösende Element sofort (z. B. die
  // Ausgeblendet-Pille), kommt kein mouseleave mehr, und die Vorschau bliebe stehen.
  // Ohne matchMedia (jsdom) bleibt sie sichtbar.
  if (typeof window.matchMedia === 'function' && window.matchMedia('(hover: none)').matches) {
    return null;
  }
  if (!card || !position) return null;

  const { width, height } = previewDimensions(card);
  const adjustedPosition = calculatePreviewPosition(
    position.x,
    position.y,
    width,
    height
  );

  return createPortal(
    <div
      className="card-preview-tooltip visible"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        width,
      }}
    >
      <UiCardImage src={card.imageUrl} alt={card.name} landscape="preview" orientation={card.imageOrientation} />
    </div>,
    document.body
  );
}

export { PREVIEW_IMAGE_WIDTH, calculatePreviewPosition };
